import React from 'react';
import { Calculator, Clock, Zap, Shield, TrendingUp, Timer } from 'lucide-react';
import { formatCurrency, formatDate, formatDurationLong } from '../utils/formatters';

export default function HomePage({ history, navigateTo, setHistory, currency, viewMeetingDetails }) {
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="mb-6">
          <Calculator className="w-16 h-16 mx-auto text-purple-600 mb-4" />
          <h1 className="text-5xl font-extrabold text-gray-900 mb-4">
            See What Your Meetings <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">REALLY</span> Cost
          </h1>
        </div>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Calculate real-time costs. Share the results. Make every meeting count.
        </p>
        
        {/* No Account Required Badge */}
        <div className="flex justify-center gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
            </svg>
            100% Free Forever
          </div>
          <div className="flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
            <Shield className="w-4 h-4" />
            No Account Required
          </div>
          <div className="flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
            <Clock className="w-4 h-4" />
            Start in 30 Seconds
          </div>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Quick Mode */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-300 hover:border-purple-500 transition-all hover:shadow-lg cursor-pointer group"
               onClick={() => navigateTo('quick-setup')}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Quick Calculate</h3>
            </div>
            <p className="text-gray-700 mb-4">
              Fast estimate with average salary and participant count. Perfect for quick checks!
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-purple-600 mr-2">✓</span>
                Takes 10 seconds to start
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-purple-600 mr-2">✓</span>
                Enter average salary only
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-purple-600 mr-2">✓</span>
                Good approximation
              </li>
            </ul>
            <div className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold text-center group-hover:bg-purple-700 transition-colors">
              Calculate →
            </div>
          </div>

          {/* Custom Mode */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-300 hover:border-blue-500 transition-all hover:shadow-lg cursor-pointer group"
               onClick={() => navigateTo('setup')}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Custom Calculate</h3>
            </div>
            <p className="text-gray-700 mb-4">
              Precise calculation with individual salaries for each participant. Most accurate!
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-blue-600 mr-2">✓</span>
                Exact cost per person
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-blue-600 mr-2">✓</span>
                Individual salary tracking
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-blue-600 mr-2">✓</span>
                Most precise results
              </li>
            </ul>
            <div className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold text-center group-hover:bg-blue-700 transition-colors">
              Calculate →
            </div>
          </div>

          {/* Fixed Duration Mode - NEW */}
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-8 border-2 border-green-300 hover:border-green-500 transition-all hover:shadow-lg cursor-pointer group"
               onClick={() => navigateTo('fixed-duration')}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-teal-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Timer className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Fixed Duration</h3>
            </div>
            <p className="text-gray-700 mb-4">
              Enter exact meeting duration and get instant results. No real-time tracking needed!
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-green-600 mr-2">✓</span>
                No real-time tracking
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-green-600 mr-2">✓</span>
                Enter hours and minutes
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <span className="text-green-600 mr-2">✓</span>
                Instant results
              </li>
            </ul>
            <div className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-center group-hover:bg-green-700 transition-colors">
              Calculate →
            </div>
          </div>
        </div>
        {/* <p className="text-sm text-gray-500 mt-4">Join 10,000+ professionals tracking meeting costs • No signup needed</p> */}
      </div>

      {/* Meeting History Section */}
      {history.length > 0 && (
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Clock className="w-6 h-6 mr-2" />
            Your Recent Meetings
          </h2>
          <div className="grid gap-4">
            {history.slice(0, 3).map(meeting => (
              <div key={meeting.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm text-gray-500">{formatDate(meeting.date)}</p>
                    <p className="text-3xl font-bold text-purple-600">{formatCurrency(meeting.totalCost, meeting.currency || currency)}</p>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>{formatDurationLong(meeting.durationSeconds || meeting.duration * 60)} • {meeting.participants} people</p>
                    <p>{formatCurrency(meeting.costPerMinute, meeting.currency || currency)}/min</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => viewMeetingDetails(meeting.id)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => {
                      // Check if it's a quick mode or fixed duration meeting
                      const isQuickMode = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isQuickMode;
                      const isFixedDuration = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isFixedDuration;
                      
                      if (isFixedDuration) {
                        // Route to fixed duration with pre-filled data
                        navigateTo('fixed-duration', { 
                          mode: meeting.participantDetails.length > 1 && meeting.participantDetails.some(p => p.name !== meeting.participantDetails[0].name) ? 'custom' : 'quick',
                          participantCount: meeting.participantDetails.length,
                          averageSalary: meeting.participantDetails[0].salaryValue,
                          salaryType: meeting.participantDetails[0].salaryType,
                          participants: meeting.participantDetails,
                          hours: Math.floor((meeting.durationSeconds || meeting.duration * 60) / 3600),
                          minutes: Math.floor(((meeting.durationSeconds || meeting.duration * 60) % 3600) / 60)
                        });
                      } else if (isQuickMode) {
                        // Route to quick setup with pre-filled data
                        navigateTo('quick-setup', { 
                          participantCount: meeting.participantDetails.length,
                          averageSalary: meeting.participantDetails[0].salaryValue,
                          salaryType: meeting.participantDetails[0].salaryType
                        });
                      } else {
                        // Route to detailed setup
                        navigateTo('setup', { participantDetails: meeting.participantDetails });
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Calculate Again
                  </button>
                </div>
              </div>
            ))}
          </div>
          {history.length > 3 && (
            <button
              onClick={() => navigateTo('history')}
              className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
            >
              View All History →
            </button>
          )}
        </div>
      )}

      {/* Problem Statement */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
        <h2 className="text-2xl font-bold text-center mb-8">The Hidden Cost of Meetings</h2>
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-4xl mb-2">💰</div>
            <div className="text-3xl font-bold text-purple-600">$37B</div>
            <div className="text-gray-600">Wasted annually on bad meetings</div>
          </div>
          <div>
            <div className="text-4xl mb-2">⏰</div>
            <div className="text-3xl font-bold text-purple-600">11M</div>
            <div className="text-gray-600">Meetings daily in the US</div>
          </div>
          <div>
            <div className="text-4xl mb-2">📊</div>
            <div className="text-3xl font-bold text-purple-600">71%</div>
            <div className="text-gray-600">Unproductive meetings</div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { num: '1', title: 'Add Participants', desc: 'Enter salaries or use role presets' },
            { num: '2', title: 'Start Meeting', desc: 'Watch costs tick up in real-time' },
            { num: '3', title: 'Share Results', desc: 'Download image or share on social' },
            { num: '4', title: 'Make Changes', desc: 'Use data to optimize meeting culture' }
          ].map((step, i) => (
            <div key={i} className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{step.num}</span>
              </div>
              <h3 className="font-bold mb-2 text-purple-700">{step.title}</h3>
              <p className="text-sm text-gray-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Why Calculate Meeting Costs Section - Redesigned */}
      <div className="mb-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Why Calculate Meeting Costs?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Make data-driven decisions about your meeting efficiency and optimize your team's productivity.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Left Card - Benefits */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Track & Optimize</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-purple-600 font-bold text-xl">✓</span>
                <span className="text-gray-700">See real costs as meetings progress</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-600 font-bold text-xl">✓</span>
                <span className="text-gray-700">Identify expensive, low-value meetings</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-600 font-bold text-xl">✓</span>
                <span className="text-gray-700">Make better decisions about attendance</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-600 font-bold text-xl">✓</span>
                <span className="text-gray-700">Promote accountability across teams</span>
              </li>
            </ul>
          </div>

          {/* Right Card - Features */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Powerful Features</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold text-xl">→</span>
                <span className="text-gray-700">Support for USD, EUR, and GBP</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold text-xl">→</span>
                <span className="text-gray-700">Visual cost comparisons</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold text-xl">→</span>
                <span className="text-gray-700">Meeting history and analytics</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold text-xl">→</span>
                <span className="text-gray-700">Share results on social media</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-purple-600 mb-1">Free</div>
              <div className="text-sm text-gray-600">Forever</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 mb-1">0s</div>
              <div className="text-sm text-gray-600">Setup Time</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 mb-1">Private</div>
              <div className="text-sm text-gray-600">No Tracking</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 mb-1">Simple</div>
              <div className="text-sm text-gray-600">Easy to Use</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center text-gray-500 text-sm border-t border-gray-200 pt-8">
        <p className="mb-2">Made with 💜 for better meetings</p>
        <p className="text-xs text-gray-400">
          © 2025 Meeting Cost Calculator. All rights reserved.
        </p>
      </footer>
    </div>
  );
}